/**
 * Combined pickup packing list — aggregates line items across N ticked
 * pickup orders in the Unassigned queue so the warehouse operator can
 * grab everything in one warehouse trip.
 *
 * Aggregation happens client-side because the parent already loaded
 * each order's `items` array from /api/dispatch/queue (which returns
 * `_aggregate_order_items` output — one row per product_id with a
 * summed quantity per order). Duplicate product_ids across orders are
 * folded into a single row here.
 *
 * The "per-order breakdown" column lets the packer still see how many
 * pieces each individual order needs while grabbing from the total.
 */
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { printHtmlViaIframe } from "@/lib/printHtml";
import { Printer } from "lucide-react";

export default function CombinedPickupPackingListModal({ open, onOpenChange, orders }) {
  // Aggregate items by product_id → { name, qty, per_order: [{order_number, qty}] }
  const byProduct = new Map();
  for (const o of orders) {
    for (const it of (o.items || [])) {
      const pid = it.product_id || it.product_name || "unknown";
      if (!byProduct.has(pid)) {
        byProduct.set(pid, { name: it.product_name || "—", qty: 0, per_order: [] });
      }
      const row = byProduct.get(pid);
      const q = it.quantity || 0;
      row.qty += q;
      row.per_order.push({ order_number: o.order_number, hotel_name: o.hotel_name, qty: q });
    }
  }
  const rows = Array.from(byProduct.values()).sort((a, b) => a.name.localeCompare(b.name));
  const totalPcs = rows.reduce((s, r) => s + r.qty, 0);

  const doPrint = () => {
    const rowsHtml = rows.map((r) => {
      const perOrder = r.per_order
        .map((po) => `<span style="display:inline-block;margin-right:8px;font-size:11px;color:#555;white-space:nowrap">${po.order_number}: <b>${po.qty}</b></span>`)
        .join("");
      return `
        <tr>
          <td>${r.name}</td>
          <td class="q">${r.qty}</td>
        </tr>
        <tr><td colspan="2" style="padding:2px 8px 8px 12px;border-bottom:1px solid #eee">${perOrder}</td></tr>
      `;
    }).join("");
    const perOrderHeader = orders
      .map((o) => `<li><b>${o.order_number}</b> · ${o.hotel_name || ""} <span style="color:#666">(${(o.items || []).reduce((s, i) => s + (i.quantity || 0), 0)} pcs)</span></li>`)
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>Combined pickup packing list</title>
      <style>
        /* 80mm thermal receipt — pure black only, no greys/purples. */
        body { font-family: -apple-system, system-ui, sans-serif; width: 72mm; margin: 0 auto; padding: 2mm; color: #000; }
        body, body * { color: #000 !important; }
        .toolbar { display: flex; gap: 8px; padding: 8px 0; border-bottom: 1px solid #000; margin-bottom: 8px; }
        .toolbar button { padding: 8px 16px; font-size: 14px; cursor: pointer; border: 1px solid #000; background: #000; color: #fff !important; border-radius: 3px; }
        .toolbar button.secondary { background: #fff; color: #000 !important; }
        h1 { margin: 0 0 4px; font-size: 17px; }
        .meta { font-size: 12px; font-weight: 700; margin-bottom: 8px; }
        ul { margin: 4px 0 10px 16px; padding: 0; font-size: 12px; }
        li { margin-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 14px; }
        th, td { padding: 4px 2px; text-align: left; }
        thead th { border-bottom: 2px solid #000; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
        td.q { text-align: right; font-weight: 800; font-family: monospace; font-size: 16px; }
        tfoot td { border-top: 2px solid #000; font-weight: 800; font-size: 16px; padding-top: 8px; }
        @page { size: 80mm auto; margin: 0; }
        @media print { .toolbar { display: none; } body { margin: 0; } }
      </style></head><body>
      <h1>Combined pickup packing list</h1>
      <div class="meta">${orders.length} pickup order${orders.length === 1 ? "" : "s"} · ${totalPcs} pcs total · ${rows.length} products</div>
      <div style="font-size:12px;color:#555;font-weight:bold;text-transform:uppercase;letter-spacing:1px;margin-top:12px">Orders included</div>
      <ul>${perOrderHeader}</ul>
      <table>
        <thead><tr><th>Product</th><th style="text-align:right">Total qty</th></tr></thead>
        <tbody>${rowsHtml || '<tr><td colspan="2" style="text-align:center;color:#888;padding:14px 0">— no line items —</td></tr>'}</tbody>
        <tfoot><tr><td>TOTAL</td><td class="q">${totalPcs}</td></tr></tfoot>
      </table>
      </body></html>`;
    printHtmlViaIframe(html);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pickup-combined-modal">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Combined pickup packing list</DialogTitle>
          <DialogDescription>
            {orders.length} pickup order{orders.length === 1 ? "" : "s"} · {totalPcs} pcs total.
            Grab this from the warehouse in one trip, then pack each order individually.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500">Orders included</div>
          <div className="flex flex-wrap gap-2">
            {orders.map((o) => (
              <span
                key={o.order_id}
                className="inline-flex items-center gap-1 text-xs bg-purple-50 border border-purple-200 text-purple-800 px-2 py-1 rounded"
                data-testid={`pickup-combined-order-${o.order_id}`}
              >
                <span className="font-mono font-semibold">{o.order_number}</span>
                <span className="text-purple-700">· {o.hotel_name}</span>
                <span className="text-purple-600">· {(o.items || []).reduce((s, i) => s + (i.quantity || 0), 0)} pcs</span>
              </span>
            ))}
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-purple-50">
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-purple-800">Product</th>
                  <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-purple-800 w-24">Total qty</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-purple-800">Per-order breakdown</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-6 text-neutral-500">No line items to pack.</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.name} className="border-t" data-testid={`pickup-combined-row-${r.name}`}>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-purple-800">{r.qty}</td>
                      <td className="px-3 py-2 text-xs text-neutral-600">
                        {r.per_order.map((po, i) => (
                          <span key={i} className="inline-block mr-3 whitespace-nowrap">
                            <span className="font-mono">{po.order_number}</span>: <b>{po.qty}</b>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-neutral-50">
                <tr>
                  <td className="px-3 py-2 font-bold uppercase tracking-widest text-xs">Total</td>
                  <td className="px-3 py-2 text-right font-mono font-extrabold text-lg text-purple-800">{totalPcs}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pickup-combined-close">
            Close
          </Button>
          <Button
            onClick={doPrint}
            disabled={rows.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="pickup-combined-print"
          >
            <Printer className="w-4 h-4 mr-1" />Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
